import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { LegalCheckFlag } from '../legal-check/types';
import { Box, styled as muiStyled } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import CustomSnackbar from '@shared/components/Snackbar';
import { ReviewStatus, ReviewStep } from '@shared/domain/review';
import { MOBILE_BREAKPOINT } from '@shared/styles/Breakpoints';
import { useApi } from '@shared/services/api';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import BasicReviewForm from '../components/review_forms/BasicReviewForm';
import EntryStrategyReviewForm from '../components/review_forms/EntryStrategyReviewForm';
import EvaluationReviewForm from '../components/review_forms/EvaluationReviewForm';
import LegalReviewForm from '../components/review_forms/LegalReviewForm';
import NegotiationReviewForm from '../components/review_forms/NegotiationReviewForm';
import PurchaseDetailsReviewForm from '../components/review_forms/PurchaseDetailsReviewForm';
import RfpReviewForm from '../components/review_forms/RfpReviewForm';

const SectionCard = muiStyled(Box)({
  background: '#fff',
  borderRadius: '12px',
  boxShadow: '0 6px 18px rgba(16, 24, 40, 0.08)',
  [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
    padding: '12px',
  },
});

interface EditReviewSectionProps {
  sectionKey: ReviewStep;
  review: any;
  updateReviewStatus?: (status: ReviewStatus) => void;
}

interface PersistedFlag {
  id: number;
  reviewId: number;
  reviewStep: number | string;
  fieldName: string;
  category: LegalCheckFlag['category'];
  excerpt: string;
  explanation: string;
  severity: LegalCheckFlag['severity'];
  detectedAt: string;
}

function EditReviewSection({
  sectionKey,
  review,
  updateReviewStatus,
}: EditReviewSectionProps) {
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const api = useApi();
  const { user, userLoadState } = useContext(UserContext);
  const [persistedFlags, setPersistedFlags] = useState<PersistedFlag[]>([]);

  const reviewId = review?.id ?? null;
  const isAdmin = user?.isAdmin === true;
  const authReady
    = userLoadState === UserLoadState.Authenticated && !!user?.idToken;

  // Fetch persisted scanner flags for the review so we can seed the form's
  // inline-per-field advisory panel with the historical observations. Admin
  // only — the endpoint 403s for everyone else, and contributors editing
  // their own review get fresh checks on type anyway.
  useEffect(() => {
    if (!isAdmin || !authReady || !reviewId) return;
    let cancelled = false;
    api
      .get(`review/${reviewId}/legal-flags`)
      .then((response: any) => {
        if (cancelled) return;
        const flags = (response?.data?.flags ?? response?.flags ?? []) as PersistedFlag[];
        setPersistedFlags(flags);
      })
      .catch(() => {
        if (!cancelled) setPersistedFlags([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, isAdmin, authReady]);

  // Group flags by field name and drop persistence-only fields. Stable
  // reference per fetched payload so useLegalCheck's effect doesn't loop.
  const seedLegalCheckFlags = useMemo<Record<string, LegalCheckFlag[]>>(() => {
    const stepNumeric = Number(sectionKey);
    const out: Record<string, LegalCheckFlag[]> = {};
    for (const f of persistedFlags) {
      // Only seed flags for the step currently being edited so we don't
      // pre-populate a basic-step textarea with a legal-step observation.
      const flagStep = typeof f.reviewStep === 'number' ? f.reviewStep : Number(f.reviewStep);
      if (flagStep !== stepNumeric) continue;
      (out[f.fieldName] ??= []).push({
        category: f.category,
        excerpt: f.excerpt,
        explanation: f.explanation,
        severity: f.severity,
      });
    }
    return out;
  }, [persistedFlags, sectionKey]);

  // Helper to map a review section to flat default values
  const mapSectionToDefaults = (section: any): Record<string, any> => {
    const out: Record<string, any> = {};
    if (!section || typeof section !== 'object') return out;

    Object.entries(section).forEach(([key, value]: [string, any]) => {
      if (key === 'ratings') return;
      if (value && typeof value === 'object' && 'answer' in value) {
        out[key] = (value as any).answer;
      }
    });

    if (Array.isArray(section.ratings)) {
      section.ratings.forEach((r: any) => {
        if (r && r.label) {
          out[r.label] =
            typeof r.answer === 'string' || typeof r.answer === 'number'
              ? r.answer
              : null;
        }
      });
    }

    return out;
  };

  const getSectionByKey = () => {
    switch (sectionKey) {
      case ReviewStep.BASIC_REVIEW:
        return review?.reviewBasic;
      case ReviewStep.PURCHASE_DETAILS:
        return review?.reviewPurchaseDetails;
      case ReviewStep.ENTRY_STRATEGY:
        return review?.reviewEntryStrategy;
      case ReviewStep.EVALUATION:
        return review?.reviewEvaluation;
      case ReviewStep.LEGAL:
        return review?.reviewLegal;
      case ReviewStep.NEGOTIATION:
        return review?.reviewNegotiation;
      case ReviewStep.RFP:
        return review?.reviewRfp;
      default:
        return null;
    }
  };

  const sectionData = getSectionByKey();
  const defaultValues: Record<string, any> = {
    id: review?.id || null,
    ...mapSectionToDefaults(sectionData),
  };

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  const handleEditSuccess = (updatedStatus?: ReviewStatus) => {
    setSnackbarConfig({
      open: true,
      type: 'success',
      message: 'Changes saved successfully.',
    });

    // Update the review status without refetching the entire review
    if (updatedStatus && updateReviewStatus) {
      updateReviewStatus(updatedStatus);
    }
  };

  return (
    <>
      <SectionCard>
        {sectionKey === ReviewStep.BASIC_REVIEW && (
          <BasicReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
            seedLegalCheckFlags={seedLegalCheckFlags}
          />
        )}
        {sectionKey === ReviewStep.PURCHASE_DETAILS && (
          <PurchaseDetailsReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
            seedLegalCheckFlags={seedLegalCheckFlags}
          />
        )}
        {sectionKey === ReviewStep.ENTRY_STRATEGY && (
          <EntryStrategyReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
            seedLegalCheckFlags={seedLegalCheckFlags}
          />
        )}
        {sectionKey === ReviewStep.EVALUATION && (
          <EvaluationReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
            seedLegalCheckFlags={seedLegalCheckFlags}
          />
        )}
        {sectionKey === ReviewStep.NEGOTIATION && (
          <NegotiationReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
            seedLegalCheckFlags={seedLegalCheckFlags}
          />
        )}
        {sectionKey === ReviewStep.LEGAL && (
          <LegalReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
            seedLegalCheckFlags={seedLegalCheckFlags}
          />
        )}
        {sectionKey === ReviewStep.RFP && (
          <RfpReviewForm
            editMode
            defaultValues={defaultValues}
            handleEditSuccess={handleEditSuccess}
            seedLegalCheckFlags={seedLegalCheckFlags}
          />
        )}
      </SectionCard>
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </>
  );
}

export default EditReviewSection;
